// Page widths (desktop): the right edge of a nav / column / list / detail / settings page is a handle. Pointer only
// (Decision css-first: pointer drag is what CSS cannot do): near an edge (mouse/pen) → `data-edge` on that page and
// on #book (CSS draws the line on the page itself and the col-resize cursor, so nothing follows scrolling here);
// drag → `--w-<kind>` on #book, which book.css/platform.css widths read; double-click → default. All kanban
// columns share one width. Kept in localStorage `hq.widths`. Touch never gets here (phones are 1-up pages).
import { type RefObject, useEffect } from "react";

const KINDS = ["nav", "col", "list", "detail", "settings"] as const;
const KEY = "hq.widths";
const ZONE = 5;
const MIN = 160;

const kindOf = (el: Element) => KINDS.find((k) => el.classList.contains(k));
const load = (): Record<string, number> => {
	try {
		return JSON.parse(localStorage.getItem(KEY) ?? "{}");
	} catch {
		return {};
	}
};

export function useResize(bookRef: RefObject<HTMLElement | null>, on: boolean) {
	useEffect(() => {
		const book = bookRef.current;
		if (!book || !on) return;
		const widths = load();
		const apply = (k: string) =>
			widths[k] ? book.style.setProperty(`--w-${k}`, `${widths[k]}px`) : book.style.removeProperty(`--w-${k}`);
		KINDS.forEach(apply);

		let hot: HTMLElement | null = null;
		let drag: { kind: string; x0: number; w0: number } | null = null;
		const mark = (el: HTMLElement | null) => {
			if (el === hot) return;
			hot?.removeAttribute("data-edge");
			hot = el;
			hot?.setAttribute("data-edge", "");
			book.toggleAttribute("data-edge", !!hot);
		};
		// the page whose right edge is within ZONE px of x (either side of the border)
		const edgeAt = (target: EventTarget | null, x: number) => {
			const page = (target as Element | null)?.closest?.("#row > section");
			if (!page) return null;
			const r = page.getBoundingClientRect();
			const el = r.right - x <= ZONE ? page : x - r.left <= ZONE ? page.previousElementSibling : null;
			return el instanceof HTMLElement && kindOf(el) ? el : null;
		};

		const onMove = (e: PointerEvent) => {
			if (drag) {
				const max = book.clientWidth - 40;
				widths[drag.kind] = Math.round(Math.min(max, Math.max(MIN, drag.w0 + e.clientX - drag.x0)));
				apply(drag.kind);
			} else if (e.pointerType !== "touch" && !e.buttons) mark(edgeAt(e.target, e.clientX));
		};
		const onDown = (e: PointerEvent) => {
			// the edge under the pointer now (the book may have scrolled since the last move)
			if (e.pointerType !== "touch") mark(edgeAt(e.target, e.clientX));
			if (!hot || e.button !== 0) return;
			e.preventDefault();
			e.stopPropagation(); // not a card drag
			drag = { kind: kindOf(hot)!, x0: e.clientX, w0: hot.offsetWidth };
			document.documentElement.toggleAttribute("data-resizing", true);
		};
		const onUp = () => {
			if (!drag) return;
			drag = null;
			document.documentElement.toggleAttribute("data-resizing", false);
			localStorage.setItem(KEY, JSON.stringify(widths));
		};
		const onDouble = (e: MouseEvent) => {
			if (!hot) return;
			const k = kindOf(hot)!;
			delete widths[k];
			apply(k);
			localStorage.setItem(KEY, JSON.stringify(widths));
			e.preventDefault();
		};
		const onLeave = () => !drag && mark(null);

		book.addEventListener("pointerleave", onLeave);
		book.addEventListener("pointerdown", onDown, true);
		book.addEventListener("dblclick", onDouble);
		document.addEventListener("pointermove", onMove);
		document.addEventListener("pointerup", onUp);
		return () => {
			mark(null);
			book.removeEventListener("pointerleave", onLeave);
			book.removeEventListener("pointerdown", onDown, true);
			book.removeEventListener("dblclick", onDouble);
			document.removeEventListener("pointermove", onMove);
			document.removeEventListener("pointerup", onUp);
			for (const k of KINDS) book.style.removeProperty(`--w-${k}`);
		};
	}, [bookRef, on]);
}
